import { getUser } from "../utils/authService";
import { useState, useEffect } from "react";
import { BookOpen, Plus, Calendar, Cloud, Sun, CloudRain, Wind, Users, Eye, Edit, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const SiteDiary = () => {
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    project_id: "",
    date: new Date().toISOString().split('T')[0],
    weather: "Sunny",
    temperature: "",
    work_description: "",
    issues_faced: "",
    safety_observations: "",
    quality_observations: "",
    visitors: [],
    visitor_name: "",
    visitor_purpose: "",
    manpower_present: "",
    equipment_deployed: "",
    materials_received: "",
    remarks: ""
  });

  // View Dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const WEATHER_OPTIONS = [
    { value: "Sunny", icon: Sun },
    { value: "Cloudy", icon: Cloud },
    { value: "Rainy", icon: CloudRain },
    { value: "Windy", icon: Wind }
  ];

  const currentUser = getUser() || {};

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedDate]);

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
        setEntryForm(prev => ({ ...prev, project_id: response.data[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const response = await apiClient.get(`/site-diary?project_id=${selectedProject}`);
      setEntries(response.data);
    } catch (error) {
      toast.error("Failed to fetch entries");
    }
  };

  const resetForm = () => {
    setEntryForm({
      project_id: selectedProject,
      date: new Date().toISOString().split('T')[0],
      weather: "Sunny",
      temperature: "",
      work_description: "",
      issues_faced: "",
      safety_observations: "",
      quality_observations: "",
      visitors: [],
      visitor_name: "",
      visitor_purpose: "",
      manpower_present: "",
      equipment_deployed: "",
      materials_received: "",
      remarks: ""
    });
    setEditingEntry(null);
  };

  const openDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setEntryForm({
        ...entry,
        visitor_name: "",
        visitor_purpose: ""
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const addVisitor = () => {
    if (entryForm.visitor_name) {
      setEntryForm(prev => ({
        ...prev,
        visitors: [...prev.visitors, { name: prev.visitor_name, purpose: prev.visitor_purpose, time: new Date().toLocaleTimeString() }],
        visitor_name: "",
        visitor_purpose: ""
      }));
    }
  };

  const removeVisitor = (index) => {
    setEntryForm(prev => ({
      ...prev,
      visitors: prev.visitors.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...entryForm,
        manpower_present: parseInt(entryForm.manpower_present) || 0,
        temperature: entryForm.temperature ? parseFloat(entryForm.temperature) : null
      };
      delete data.visitor_name;
      delete data.visitor_purpose;

      if (editingEntry) {
        await apiClient.put(`/site-diary/${editingEntry.id}`, data);
        toast.success("Entry updated");
      } else {
        await apiClient.post('/site-diary', data);
        toast.success("Entry created");
      }
      setDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save entry");
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm("Delete this diary entry?")) return;
    try {
      await apiClient.delete(`/site-diary/${entryId}`);
      toast.success("Entry deleted");
      fetchEntries();
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  const getWeatherIcon = (weather) => {
    const option = WEATHER_OPTIONS.find(w => w.value === weather);
    return option ? option.icon : Sun;
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">SITE DIARY</h1>
          <p className="text-slate-600 mt-2 font-body">Daily site observations, visitors, and work progress</p>
        </div>
        <button onClick={() => openDialog()} className="btn-primary flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>New Entry</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* Entries List */}
      <div className="space-y-4">
        {entries.length > 0 ? entries.map(entry => {
          const WeatherIcon = getWeatherIcon(entry.weather);
          return (
            <div key={entry.id} className="stat-card">
              <div className="flex justify-between items-start">
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <p className="font-heading font-bold text-2xl text-primary">{new Date(entry.date).getDate()}</p>
                    <p className="text-xs text-slate-600">{new Date(entry.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="border-l border-slate-300 pl-4">
                    <div className="flex items-center space-x-2 mb-1">
                      <WeatherIcon className="w-5 h-5 text-warning" />
                      <span className="text-sm">{entry.weather}</span>
                      {entry.temperature && <span className="text-sm text-slate-500">| {entry.temperature}°C</span>}
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-2">{entry.work_description || 'No description'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <p className="font-bold text-lg text-primary">{entry.manpower_present || 0}</p>
                    <p className="text-xs text-slate-500">Workers</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-lg text-accent">{entry.visitors?.length || 0}</p>
                    <p className="text-xs text-slate-500">Visitors</p>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => { setSelectedEntry(entry); setViewDialogOpen(true); }} className="p-2 hover:bg-slate-100 rounded">
                      <Eye className="w-4 h-4 text-primary" />
                    </button>
                    <button onClick={() => openDialog(entry)} className="p-2 hover:bg-slate-100 rounded">
                      <Edit className="w-4 h-4 text-accent" />
                    </button>
                    <button onClick={() => deleteEntry(entry.id)} className="p-2 hover:bg-red-100 rounded">
                      <Trash2 className="w-4 h-4 text-error" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }) : (
          <div className="text-center py-16 stat-card">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-400" />
            <h3 className="font-heading font-bold text-xl mb-2">No Diary Entries</h3>
            <p className="text-slate-600">Start recording daily site observations.</p>
          </div>
        )}
      </div>

      {/* Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editingEntry ? 'EDIT' : 'NEW'} SITE DIARY ENTRY</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date *</label>
                <input
                  type="date"
                  required
                  value={entryForm.date}
                  onChange={(e) => setEntryForm({...entryForm, date: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Weather *</label>
                <select
                  required
                  value={entryForm.weather}
                  onChange={(e) => setEntryForm({...entryForm, weather: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                >
                  {WEATHER_OPTIONS.map(w => <option key={w.value} value={w.value}>{w.value}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Temperature (°C)</label>
                <input
                  type="number"
                  value={entryForm.temperature}
                  onChange={(e) => setEntryForm({...entryForm, temperature: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                  placeholder="e.g., 32"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Work Description *</label>
              <textarea
                required
                value={entryForm.work_description}
                onChange={(e) => setEntryForm({...entryForm, work_description: e.target.value})}
                className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                rows={3}
                placeholder="Describe work done today..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Issues Faced</label>
                <textarea
                  value={entryForm.issues_faced}
                  onChange={(e) => setEntryForm({...entryForm, issues_faced: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Safety Observations</label>
                <textarea
                  value={entryForm.safety_observations}
                  onChange={(e) => setEntryForm({...entryForm, safety_observations: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                  rows={2}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Manpower Present</label>
                <input
                  type="number"
                  value={entryForm.manpower_present}
                  onChange={(e) => setEntryForm({...entryForm, manpower_present: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Equipment Deployed</label>
                <input
                  type="text"
                  value={entryForm.equipment_deployed}
                  onChange={(e) => setEntryForm({...entryForm, equipment_deployed: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Materials Received</label>
                <input
                  type="text"
                  value={entryForm.materials_received}
                  onChange={(e) => setEntryForm({...entryForm, materials_received: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                />
              </div>
            </div>

            {/* Visitors */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Visitors Log</label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={entryForm.visitor_name}
                  onChange={(e) => setEntryForm({...entryForm, visitor_name: e.target.value})}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-sm"
                  placeholder="Visitor Name"
                />
                <input
                  type="text"
                  value={entryForm.visitor_purpose}
                  onChange={(e) => setEntryForm({...entryForm, visitor_purpose: e.target.value})}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-sm"
                  placeholder="Purpose"
                />
                <button type="button" onClick={addVisitor} className="btn-outline">Add</button>
              </div>
              {entryForm.visitors.length > 0 && (
                <div className="space-y-1">
                  {entryForm.visitors.map((v, idx) => (
                    <div key={`visitor-${v.name}-${idx}`} className="flex items-center justify-between bg-slate-50 p-2 rounded-sm">
                      <span className="text-sm"><strong>{v.name}</strong> - {v.purpose}</span>
                      <button type="button" onClick={() => removeVisitor(idx)} className="text-error text-xs">Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Remarks</label>
              <textarea
                value={entryForm.remarks}
                onChange={(e) => setEntryForm({...entryForm, remarks: e.target.value})}
                className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                rows={2}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setDialogOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary">{editingEntry ? 'Update' : 'Save'} Entry</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">DIARY ENTRY - {selectedEntry && new Date(selectedEntry.date).toLocaleDateString()}</DialogTitle>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><strong>Weather:</strong> {selectedEntry.weather} {selectedEntry.temperature && `(${selectedEntry.temperature}°C)`}</div>
                <div><strong>Manpower:</strong> {selectedEntry.manpower_present || 0}</div>
                <div><strong>Visitors:</strong> {selectedEntry.visitors?.length || 0}</div>
              </div>
              <div><strong>Work Description:</strong><p className="text-sm text-slate-700 mt-1">{selectedEntry.work_description || 'N/A'}</p></div>
              {selectedEntry.issues_faced && <div><strong>Issues:</strong><p className="text-sm text-slate-700 mt-1">{selectedEntry.issues_faced}</p></div>}
              {selectedEntry.safety_observations && <div><strong>Safety:</strong><p className="text-sm text-slate-700 mt-1">{selectedEntry.safety_observations}</p></div>}
              {selectedEntry.visitors?.length > 0 && (
                <div>
                  <strong>Visitors:</strong>
                  <div className="mt-1 space-y-1">
                    {selectedEntry.visitors.map((v, idx) => (
                      <div key={`view-visitor-${v.name}-${idx}`} className="text-sm bg-slate-50 p-2 rounded-sm">{v.name} - {v.purpose}</div>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs text-slate-500">Recorded by: {selectedEntry.recorded_by}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SiteDiary;
